import { ManagedField, Tokens, User, Video, FieldUser, Notification, Field, AdminField, AdminFieldUser, AdminFieldWithMetrics, AdminRegisteredField, AdminGlobalSummary, LiveFieldsResponse, AdminUserItem, AdminUsersResponse, AdminEquipamentsOptionsResponse, AdminCreatedEquipament, AdminEquipamentsResponse, AdminEquipamentListItem, AdminCreatedField, AdminFieldCourtPayload, AdminCreatedFieldCourt, AdminBillingCostsResponse, PublicImpactMetrics } from '../types/types';
import api from './api';
import { getClientId } from '../utils/clientId';

export const loginUser = async (
  email: string,
  password: string
): Promise<Tokens> => {
  const { data } = await api.post<Tokens>('/auth/login', { email, password });
  return data;                                      // { accessToken, refreshToken }
};

/* ---------- Campos de futebol ---------- */
export const fetchFootballFields = async (token?: string): Promise<Field[]> => {
  const { data } = await api.get('/fields/all', {
    headers: token ? { 'Authorization': `Bearer ${token}` } : {}
  });
  return data;
};

export const fetchPublicImpactMetrics = async (): Promise<PublicImpactMetrics> => {
  const { data } = await api.get<PublicImpactMetrics>('/public/impact');
  return data;
};

/* ---------- Vídeos por campo ---------- */
export const fetchVideosByField = async (fieldId: number): Promise<Video[]> => {
  const { data } = await api.get(`/videos/all/${fieldId}`);
  return data;
};

/* ---------- Envio de ticket ---------- */
export const sendTicketForm = async (
  name: string,
  email: string,
  phone: string,
  subject: string,
  message: string
) => {
  const { status } = await api.post('/tickets', { name, email, subject, phone, message });
  return status;
};

/* ---------- Vídeos por quadra e dia ---------- */
export const fetchVideosByCourt = async (courtId: number | undefined, defaultDay: string, authenticated: boolean): Promise<Video[]> => {

  let result;

  if (authenticated) {
    console.log('-------------')
    console.log('authenticated', authenticated)
    console.log('-------------')
    result = await api.get(`/videos/all/court/${courtId}/${defaultDay}/user`);
  }
  else {
    console.log('-------------')
    console.log('not authenticated')
    console.log('-------------')
    result = await api.get(`/videos/all/court/${courtId}/${defaultDay}`);
  }


  return result.data;
};

export const getFieldsAndCourtsManaged = async (
  token: string
): Promise<ManagedField[]> => {
  const { data } = await api.get(`/fields/managed`, {
    headers: { 'Authorization': `Bearer ${token}` }
  });

  return data;
};


/* ---------- Sistema Unificado de Eventos ---------- */
export interface EventParams {
  eventType: 'view' | 'share' | 'download' | 'like' | 'view-share';
  videoId?: number;
  fieldId?: number;
  courtId?: number;
  clientId?: string;
  meta?: Record<string, any>;
}

export interface LikeResponse {
  likesCount: number;
  liked: boolean;
}

export const registerEvent = async <T = any>(params: EventParams, token?: string, useKeepAlive = false): Promise<T | null> => {
  try {
    const payload = {
      ...params,
      clientId: params.clientId || getClientId(),
    };

    // Se useKeepAlive for true, usamos a Fetch API pura com keepalive para garantir envio na saída do app
    if (useKeepAlive && typeof fetch !== 'undefined') {
      const url = `${api.defaults.baseURL}/events`;
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
        keepalive: true
      }).catch(err => console.error('Erro no keepalive event:', err));

      return null;
    }

    // Caso contrário, usamos o axios padrão
    const { data } = await api.post('/events', payload, {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {}
    });

    return data.data as T; // O backend retorna { message, data: { ... } }
  } catch (error) {
    console.error(`Erro ao registrar evento ${params.eventType}:`, error);
    return null;
  }
};

/* ---------- Download de vídeo ---------- */
export const downloadVideo = async (
  videoId: number,
  courtId: number,
  fieldId: number,
  date: string,
  token?: string,
  userId?: number
) => {
  await registerEvent({
    eventType: 'download',
    videoId,
    courtId,
    fieldId,
    meta: { date, userId }
  }, token);
};

/* ---------- Incrementar visualização ---------- */
export const incrementView = async (
  videoId: number,
  courtId: number,
  fieldId: number,
  token?: string,
  source?: string
) => {
  await registerEvent({
    eventType: 'view',
    videoId,
    courtId,
    fieldId,
    meta: { source }
  }, token);
};

export const incrementLike = async (videoId: number, token: string, fieldId?: number, courtId?: number): Promise<LikeResponse | null> => {
  return await registerEvent<LikeResponse>({
    eventType: 'like',
    videoId,
    fieldId,
    courtId
  }, token);
};

export const registerShare = async (videoId: number, fieldId: number, courtId: number, token?: string, useKeepAlive = false) => {
  await registerEvent({
    eventType: 'share',
    videoId,
    fieldId,
    courtId
  }, token, useKeepAlive);
};


export const fetchPublicVideo = async (videoId: string): Promise<{ video: Video, field: Field, court: { courtId: number, courtName: string } }> => {
  const { data } = await api.get(`/videos/public/${videoId}`);
  return data;
};


export const registerUser = async (
  name: string,
  email: string,
  password: string,
  termsAcceptedAt: string,
  termsVersion: string,
  privacyAcceptedAt: string,
  privacyVersion: string,
): Promise<Tokens> => {
  try {
    const { data } = await api.post<Tokens>('/auth/register', {
      name,
      email,
      password,
      termsAcceptedAt,
      termsVersion,
      privacyAcceptedAt,
      privacyVersion
    });

    return data;                      // ✅ agora é Tokens
  } catch (err) {
    console.error('Erro ao criar usuário:', err);
    throw err;                        // propaga para o componente tratar
  }
};

/* ---------- 1) Atualizar perfil (nome, telefone, idioma) ---------- */
export const updateUserProfile = async (
  payload: Partial<Pick<User, 'name' | 'phone' | 'language'>>
): Promise<User> => {
  const { data } = await api.put<User>('/user', payload);
  return data;
};
/* ---------- 2) Atualizar preferências de notificação ---------- */
export const updateNotificationPrefs = async (notifyNewVideos: boolean, notifAppointments: boolean, token: string): Promise<{ push: boolean; email: boolean }> => {
  const { data } = await api.patch('/users/update', { notifyNewVideos, notifAppointments }, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  return data;
};

/* ---------- 3) Enviar nova foto de perfil ---------- */
export const uploadProfileImage = async (file: File, token: string): Promise<{ profileImageUrl: string }> => {
  const form = new FormData();
  form.append('profileImage', file);
  const { data } = await api.patch('/users/update', form, {
    headers: {
      'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${token}`
    }
  });
  return data;
};

/* ---------- 4) Deletar conta ---------- */
export const deleteAccount = async (): Promise<{ success: true }> => {
  const { data } = await api.delete('/user');
  return data;
};

/**-- ALTERAR SENHA ──────────────────────────────────────────────*/
export const changePassword = async (token: string, payload: {
  currentPassword: string;
  newPassword: string;
  confirm: string;
}): Promise<void> => {
  await api.put('/users/me/password', payload, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
};

export const confirmPasswordReset = async (
  token: string,
  newPassword: string
): Promise<{ ok: true }> => {
  const { data } = await api.post('/auth/password-reset/confirm', {
    token,
    newPassword,
  });
  return data; // { ok: true }
};

export const requestPasswordReset = async (email: string): Promise<{ ok: true }> => {
  const { data } = await api.post('/auth/password-reset/request', { email });
  return data;
};

export const updateFieldData = async (payload: Partial<Pick<ManagedField, 'block' | 'fieldId'>>, token: string): Promise<any> => {

  const { data } = await api.patch<User>(`/fields/managed/${payload.fieldId}`, payload, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  })

  return data;
};

/* ---------- Gestão de Membros do Campo (Manager) ---------- */
export const listManagedFieldUsers = async (fieldId: number, token: string): Promise<FieldUser[]> => {
  const { data } = await api.get(`/fields/managed/${fieldId}/users`, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  return data;
};

export const inviteManagedFieldUser = async (fieldId: number, email: string, token: string): Promise<void> => {
  await api.post(`/fields/managed/${fieldId}/users`, { email }, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
};

export const removeManagedFieldUser = async (fieldId: number, userId: number, token: string): Promise<void> => {
  await api.delete(`/fields/managed/${fieldId}/users/${userId}`, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
};


/* ---------- Notificações e Convites ---------- */
export const savePushSubscription = async (subscription: PushSubscription, token: string): Promise<void> => {
  await api.post('/notifications/subscribe', { subscription }, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
};

export const deletePushSubscription = async (endpoint: string, token: string): Promise<void> => {
  // O backend pode usar o endpoint para identificar qual subscrição remover
  await api.post('/notifications/unsubscribe', { endpoint }, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
};

export const fetchNotifications = async (token: string): Promise<Notification[]> => {
  const { data } = await api.get('/notifications', {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  return data;
};

export const markNotificationsAsRead = async (token: string): Promise<void> => {
  await api.put('/notifications/read-all', {}, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
};

export const markNotificationAsRead = async (id: number, token: string): Promise<void> => {
  await api.put(`/notifications/${id}/read`, {}, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
};


export const respondToInvitation = async (fieldId: number, accept: boolean, token: string): Promise<void> => {
  await api.post(`/users/invitations/${fieldId}/respond`, { accept }, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
};

export const verifyFieldAccess = async (fieldId: number | undefined, token: string): Promise<{ allowed: boolean, status: string }> => {
  if (!fieldId) return { allowed: false, status: 'NONE' };
  const { data } = await api.get(`/fields/managed/access/${fieldId}`, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  return data;
};

/* ---------- Favoritar Campo ---------- */
export const toggleFavoriteField = async (fieldId: number, favorite: boolean, token: string): Promise<void> => {
  await api.post(`/fields/favorite/${fieldId}`, { favorite }, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
};

/* ---------- Listar Campos Favoritos ---------- */
export const fetchFavoriteFields = async (token: string): Promise<Field[]> => {
  const { data } = await api.get('/fields/favorites', {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  return data;
};

/* ---------- ADMIN APIs ---------- */

export const getAdminFields = async (token: string): Promise<AdminField[]> => {
  const { data } = await api.get('/admin/fields', {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  return data;
};

export interface CreateAdminFieldPayload {
  name: string;
  city: string;
  address: string;
  logo?: string;
  active?: boolean;
  block?: boolean;
  courts?: AdminFieldCourtPayload[];
  managerUserIds?: number[];
}

export const createAdminField = async (payload: CreateAdminFieldPayload, token: string): Promise<AdminCreatedField> => {
  const { data } = await api.post('/admin/fields', payload, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  return data;
};

export interface UpdateAdminFieldPayload {
  name?: string;
  city?: string;
  address?: string;
  logo?: string;
  active?: boolean;
  block?: boolean;
}

export const updateAdminField = async (fieldId: number, payload: UpdateAdminFieldPayload, token: string): Promise<AdminField> => {
  const { data } = await api.patch(`/admin/fields/${fieldId}`, payload, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  return data;
};

export const deleteAdminField = async (fieldId: number, token: string): Promise<void> => {
  await api.delete(`/admin/fields/${fieldId}`, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
};

export const createAdminFieldCourt = async (fieldId: number, payload: AdminFieldCourtPayload, token: string): Promise<AdminCreatedFieldCourt> => {
  const { data } = await api.post(`/admin/fields/${fieldId}/courts`, payload, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  return data;
};

export const getAdminFieldUsers = async (fieldId: number, token: string): Promise<AdminFieldUser[]> => {
  const { data } = await api.get(`/admin/fields/${fieldId}/users`, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  return data;
};

export const getAdminFieldMetrics = async (fieldId: number, token: string): Promise<AdminFieldWithMetrics> => {
  const { data } = await api.get(`/admin/fields/${fieldId}/metrics`, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  return data;
};

export const getAdminRegisteredFields = async (token: string): Promise<AdminRegisteredField[]> => {
  const { data } = await api.get('/admin/fields/registered', {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  return data;
};

export const getAdminEquipamentsOptions = async (token: string): Promise<AdminEquipamentsOptionsResponse> => {
  const { data } = await api.get('/admin/equipaments/options', {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  return data;
};

export interface GetAdminEquipamentsParams {
  page?: number;
  pageSize?: number;
  search?: string;
  active?: boolean;
  fieldId?: number;
  courtId?: number;
}

export const getAdminEquipaments = async (params: GetAdminEquipamentsParams, token: string): Promise<AdminEquipamentsResponse> => {
  const { data } = await api.get('/admin/equipaments', {
    params,
    headers: { 'Authorization': `Bearer ${token}` }
  });
  return data;
};

export interface CreateAdminEquipamentPayload {
  fieldId: number;
  courtId: number;
  city?: string;
  type?: string;
  active?: boolean;
}

export const createAdminEquipament = async (payload: CreateAdminEquipamentPayload, token: string): Promise<AdminCreatedEquipament> => {
  const { data } = await api.post('/admin/equipaments', payload, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  return data;
};

export interface UpdateAdminEquipamentPayload {
  fieldId?: number;
  courtId?: number;
  city?: string;
  type?: string;
  active?: boolean;
}

export const updateAdminEquipament = async (
  equipamentId: number,
  payload: UpdateAdminEquipamentPayload,
  token: string
): Promise<AdminEquipamentListItem> => {
  const { data } = await api.patch(`/admin/equipaments/${equipamentId}`, payload, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  return data;
};

export const getAdminGlobalMetrics = async (token: string): Promise<AdminGlobalSummary> => {
  const { data } = await api.get('/admin/metrics/summary', {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  return data;
};

export interface GetAdminBillingCostsParams {
  month?: string;
  from?: string;
  to?: string;
  courtId?: number;
}

export const getAdminBillingCosts = async (
  params: GetAdminBillingCostsParams,
  token: string
): Promise<AdminBillingCostsResponse> => {
  const { data } = await api.get('/admin/billing/costs', {
    params,
    headers: { 'Authorization': `Bearer ${token}` }
  });
  return data;
};

export interface GetAdminUsersParams {
  type: 1 | 2 | 3;
  page?: number;
  pageSize?: number;
  search?: string;
}

export const getAdminUsers = async (params: GetAdminUsersParams, token: string): Promise<AdminUsersResponse> => {
  const { data } = await api.get('/admin/users', {
    params,
    headers: { 'Authorization': `Bearer ${token}` }
  });
  return data;
};

export interface UpdateAdminUserPayload {
  name?: string;
  email?: string;
  username?: string;
  role?: string;
  temporaryPassword?: string;
}

export const updateAdminUser = async (userId: number, payload: UpdateAdminUserPayload, token: string): Promise<AdminUserItem> => {
  const { data } = await api.patch(`/admin/users/${userId}`, payload, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  return data;
};

export interface CreateAdminUserPayload {
  email: string;
  temporaryPassword: string;
  name?: string;
  role?: string;
}

export const createAdminUser = async (payload: CreateAdminUserPayload, token: string): Promise<AdminUserItem> => {
  const { data } = await api.post('/admin/users', payload, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
  return data;
};

export const deleteAdminUser = async (userId: number, token: string): Promise<void> => {
  await api.delete(`/admin/users/${userId}`, {
    headers: { 'Authorization': `Bearer ${token}` }
  });
};

export interface FetchLiveFieldsParams {
  page?: number;
  pageSize?: number;
  fieldId?: number;
  courtId?: number;
  equipamentId?: number;
  city?: string;
  platform?: string;
  dateFrom?: string;
  dateTo?: string;
  status?: 'ACTIVE' | 'ENDED' | 'ALL';
}

/* ---------- Lives por campo ---------- */
export const fetchLiveFields = async (params: FetchLiveFieldsParams = {}): Promise<LiveFieldsResponse> => {
  const { data } = await api.get('/lives/fields', { params });
  return data;
};
